import { Avatar, Button, Drawer, Form, Input, Popconfirm, Skeleton, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import AccountManagementCustomerService from "../../services/AccountManagementCustomerService";
import { IAccountManagementCustomerNote } from "../../types/AccountManagementCustomer";
import { can, openNotification } from "../../utils";
import "../../pages/AccountManagementCustomerDetail.css";

const { TextArea } = Input;

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#059669", "#0891b2"];

const getAvatarColor = (seed: string) => {
  const code = seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
  const parts = name.split(/\s+|@|\./).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatDayTime = (value?: string) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value;
};

interface NotesProps {
  customerId: number;
}

const Notes = ({ customerId }: NotesProps) => {
  const [notes, setNotes] = useState<IAccountManagementCustomerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<IAccountManagementCustomerNote | null>(null);
  const [form] = Form.useForm();

  const loadNotes = useCallback(() => {
    setLoading(true);
    AccountManagementCustomerService.getNotes(customerId)
      .then((response) => setNotes(response.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const openCreateDrawer = () => {
    setEditingNote(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEditDrawer = (note: IAccountManagementCustomerNote) => {
    setEditingNote(note);
    form.setFieldsValue({ description: note.description });
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingNote) {
        await AccountManagementCustomerService.updateNote(editingNote.id, values);
        openNotification("success", "Success", "Note updated");
      } else {
        await AccountManagementCustomerService.createNote(customerId, values);
        openNotification("success", "Success", "Note created");
      }
      form.resetFields();
      setDrawerOpen(false);
      setEditingNote(null);
      loadNotes();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = (noteId: number) => {
    AccountManagementCustomerService.deleteNote(noteId)
      .then(() => {
        openNotification("success", "Success", "Note deleted");
        loadNotes();
      })
      .catch(console.error);
  };

  const canCreate = can("account-management-customers:notes:create");
  const canUpdate = can("account-management-customers:notes:update");
  const canDelete = can("account-management-customers:notes:delete");

  return (
    <div className="acmd-card">
      <div className="acmd-card-head">
        <span className="acmd-card-title">
          Notlar
          <span className="acmd-card-title-count">{notes.length}</span>
        </span>
        {canCreate && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateDrawer}>
            Not Ekle
          </Button>
        )}
      </div>

      {loading && notes.length === 0 ? (
        <div className="acmd-card-body">
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : notes.length === 0 ? (
        <div className="acmd-empty">Henüz not eklenmemiş</div>
      ) : (
        <div className="acmd-notes-list">
          {notes.map((note) => {
            const author = note.user?.name || note.user?.username || note.createdBy || "Bilinmiyor";
            const wasEdited = Boolean(note.updatedAt && note.updatedAt !== note.createdAt);
            return (
              <div key={note.id} className="acmd-note">
                <div className="acmd-note-head">
                  <Avatar
                    size={24}
                    className="acmd-note-avatar"
                    style={{ backgroundColor: getAvatarColor(author) }}
                  >
                    {getInitials(author)}
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    <div className="acmd-note-author" title={author}>
                      {author}
                    </div>
                    <div className="acmd-note-time">
                      {formatDayTime(note.createdAt)}
                      {wasEdited && (
                        <Tooltip
                          title={`Düzenlendi: ${formatDayTime(note.updatedAt)}${
                            note.updatedBy ? ` · ${note.updatedBy}` : ""
                          }`}
                        >
                          <span> · düzenlendi</span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className="acmd-note-actions">
                    {canUpdate && (
                      <Button
                        type="text"
                        className="acmd-note-action"
                        icon={<EditOutlined />}
                        onClick={() => openEditDrawer(note)}
                      />
                    )}
                    {canDelete && (
                      <Popconfirm
                        title="Not silinsin mi?"
                        okText="Sil"
                        cancelText="Vazgeç"
                        onConfirm={() => handleDelete(note.id)}
                      >
                        <Button
                          type="text"
                          className="acmd-note-action acmd-note-action--danger"
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    )}
                  </div>
                </div>
                <div className="acmd-note-body">{note.description}</div>
              </div>
            );
          })}
        </div>
      )}

      <Drawer
        title={editingNote ? "Notu Düzenle" : "Not Ekle"}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingNote(null);
        }}
        width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="description" label="Not" rules={[{ required: true, message: "Zorunlu" }]}>
            <TextArea rows={7} placeholder="Notunuzu yazın…" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Kaydet
          </Button>
        </Form>
      </Drawer>
    </div>
  );
};

export default Notes;
